import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';
import './Documents.css';
import { useGetDocumentHistory } from '../../../services/loanService';
import { DocumentHistoryPage, GetDocumentHistoryStatus } from '../../../services/schemas/loanServiceSchemas';
import { formatCurrency, formatDate } from '../../../utils/formatters';

const Documents: React.FC = () => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<GetDocumentHistoryStatus | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const ALL_DOCUMENTS_PAGE_SIZE = 10000;

  // Use the documentHistory query hook
  const { data, isLoading, isError, error } = useGetDocumentHistory({
    page: 0,
    size: ALL_DOCUMENTS_PAGE_SIZE
  });

  // Get status badge color based on status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VERIFICATION_COMPLETE':
      case 'AGREEMENT_READY':
      case 'ACTIVE':
      case 'COMPLETED':
        return 'success';
      case 'VERIFICATION_REJECTED':
      case 'REJECTED_EVALUATION':
      case 'REJECTED_COLLATERAL':
      case 'REJECTED':
      case 'CANCELLED':
        return 'danger';
      case 'DRAFT':
      case 'PENDING_SUBMISSION':
      case 'SUBMITTED':
      case 'PENDING_DISBURSEMENT':
        return 'warning';
      case 'LEAD_PROCESSING':
      case 'DOCUMENT_COLLECTION':
      case 'VERIFICATION_IN_PROGRESS':
      case 'EVALUATION_IN_PROGRESS':
      case 'AGREEMENT_PREPARATION':
      case 'COLLATERAL_VERIFICATION':
      case 'DISBURSEMENT_IN_PROGRESS':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getDocumentProgress = (count: number, required: number) => {
    const percentage = Math.min(Math.round((count / required) * 100), 100);
    return (
      <div className="document-progress">
        <div className="progress" style={{ height: '8px' }}>
          <div
            className={`progress-bar ${percentage === 100 ? 'bg-success' : percentage > 50 ? 'bg-warning' : 'bg-danger'}`}
            role="progressbar"
            style={{ width: `${percentage}%` }}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <small className="text-muted d-block mt-1">
          {count} of {required} documents
        </small>
      </div>
    );
  };

  const columns = [
    {
      name: 'Application ID',
      selector: (row: any) => row.applicationNumber,
      sortable: true,
      cell: (row: any) => (
        <div>
          <div className="fw-bold">#{row.applicationNumber}</div>
        </div>
      ),
    },
    {
      name: 'Customer Name',
      selector: (row: any) => row.customerName,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row: any) => row.status,
      sortable: true,
      cell: (row: any) => (
        <span className={`badge bg-${getStatusColor(row.status)}`}>
          {row.status.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      name: 'Documents',
      selector: (row: any) => row.documentCount,
      sortable: true,
      cell: (row: any) => getDocumentProgress(row.documentCount, row.requiredDocuments),
    },
    {
      name: 'Last Updated',
      selector: (row: any) => row.originalLastUpdated,
      sortable: true,
      format: (row: any) => formatDate(row.originalLastUpdated),
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => navigate(`/loan-application-documents/view/${row.id}`)}
            data-tooltip-id={`view-${row.id}`}
          >
            <i className="bi bi-eye"></i>
          </button>
          <Tooltip id={`view-${row.id}`} place="top" content="View documents" />

          {row.status === 'DOCUMENT_COLLECTION' && (
            <button
              className="btn btn-sm btn-info"
              onClick={() => navigate(`/loan-application-documents/${row.id}`)}
              data-tooltip-id={`upload-${row.id}`}
            >
              <i className="bi bi-upload"></i>
            </button>
          )}
          <Tooltip id={`upload-${row.id}`} place="top" content="Upload documents" />
        </div>
      ),
    },
  ];

  // Transform API data to match our component's expected format
  const transformedData = data?.content?.map((item: any) => ({
    id: item.id,
    customerName: item.customerName || 'Unknown Customer',
    applicationNumber: item.applicationNumber || 'N/A',
    status: item.status,
    documentCount: item.documentsCount || 0,
    requiredDocuments: item.requiredDocumentsCount || 0,
    lastUpdated: formatDate(item.lastUpdated),
    originalLastUpdated: item.lastUpdated, // Keep original date for sorting
    missingDocuments: item.missingDocuments || [],
  })) || [];

  // Filter applications based on search query
  const filteredApplications = transformedData.filter((app: any) => {
    const matchesSearch = !searchQuery ||
      app.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicationNumber.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Get summary counts
  const getTotalCount = () => filteredApplications.length;

  // Pending applications include draft and pending submission statuses
  const getPendingCount = () => data?.content?.filter((item: any) =>
    ['DRAFT', 'PENDING_SUBMISSION', 'SUBMITTED', 'PENDING_DISBURSEMENT'].includes(item.status)
  ).length || 0;

  // In Progress applications include all verification, evaluation and processing statuses
  const getInProgressCount = () => data?.content?.filter((item: any) =>
    ['LEAD_PROCESSING', 'DOCUMENT_COLLECTION', 'VERIFICATION_IN_PROGRESS',
     'EVALUATION_IN_PROGRESS', 'AGREEMENT_PREPARATION', 'COLLATERAL_VERIFICATION',
     'DISBURSEMENT_IN_PROGRESS'].includes(item.status)
  ).length || 0;

  // Active and completed applications
  const getCompletedCount = () => data?.content?.filter((item: any) =>
    ['VERIFICATION_COMPLETE', 'AGREEMENT_READY', 'ACTIVE', 'COMPLETED'].includes(item.status)
  ).length || 0;

  // Rejected applications in any stage
  const getRejectedCount = () => data?.content?.filter((item: any) =>
    ['VERIFICATION_REJECTED', 'REJECTED_EVALUATION', 'REJECTED_COLLATERAL',
     'REJECTED', 'CANCELLED'].includes(item.status)
  ).length || 0;

  // Calculate stats for display
  const stats = [
    { title: 'Total Applications', value: getTotalCount(), icon: 'bi-file-earmark-text', color: 'primary' },
    { title: 'Pending', value: getPendingCount(), icon: 'bi-clock', color: 'warning' },
    { title: 'In Progress', value: getInProgressCount(), icon: 'bi-gear', color: 'info' },
    { title: 'Completed', value: getCompletedCount(), icon: 'bi-check-circle', color: 'success' }
  ];


  return (
    <div className="documents-page">
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Document Management</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Summary Cards */}
          <div className="row g-3 mb-3 row-deck">
            {stats.map((stat, index) => (
              <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                <div className="card">
                  <div className="card-body">
                    <i className={`bi ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                    <div className="mb-2 text-uppercase">{stat.title}</div>
                    <div>
                      <span className="h4">
                        {isLoading ? <Spinner animation="border" size="sm" /> : stat.value}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading application documents...</p>
                    </div>
                  ) : isError ? (
                    <div className="alert alert-danger m-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Error loading document history: {(error as any)?.message || 'Unknown error'}
                    </div>
                  ) : filteredApplications.length === 0 ? (
                    <div className="alert alert-info m-3">
                      <i className="bi bi-info-circle me-2"></i>
                      No document history found. Try changing your filters.
                    </div>
                  ) : (
                      <DataTable
                        columns={columns}
                        data={filteredApplications}
                        pagination
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      responsive
                      striped
                      highlightOnHover
                      noDataComponent={
                        <div className="text-center py-4">
                          <i className="bi bi-folder text-muted" style={{ fontSize: '2rem' }}></i>
                          <p className="mt-2">No applications found</p>
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
